/**
 * useScheduleAggregates — derive the Schedule-page aggregates that
 * sit *above* and *below* the Facility Schedule table, and push them
 * into the form so the read-only surfaces (SummaryCards,
 * StateDistributionSection) pick them up via `useWatch`.
 *
 * Computed values (all client-side from `useFacilitiesCalc`). Every sum runs
 * over NON-ARCHIVED facilities only — an archived facility is excluded from
 * everything pricing-related (OR-1431):
 *
 *   Top-level (SummaryCards):
 *     rateUnadjusted          = Σ Unadj. Premium / Σ Equivalency
 *     rateAdjustedOccupancy   = Σ SIR Adj. Premium / Σ Equivalency
 *
 *   State Distribution (per unique state):
 *     exposure                = Σ Equivalency for that state
 *     rates                   = Σ Unadj. Premium / Σ Equivalency for that state
 *     (% Overall is computed at render time from `exposure / Σ exposure`)
 *
 * The hook lives one level above the form sections so it has a
 * single view of every facility — sections read from the form, this
 * hook writes. Empty / zero denominators emit "" so the UI falls
 * back to the placeholder ("—") rather than rendering NaN/Infinity.
 */
import { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useFacilities } from "../../../hooks/use-facilities";
import type { Facility } from "../../../types/facility";
import type { ScheduleFormData } from "./schema";
import { useFacilitiesCalc } from "./use-facility-calc";

type StateDistributionRow = ScheduleFormData["stateDistribution"][number];

const EMPTY_FACILITIES: Facility[] = [];

function formatRate(value: number): string {
    if (!Number.isFinite(value) || value === 0) return "";
    return value.toFixed(2);
}

/** One decimal so the value stays reconcilable with the Facility
 *  Schedule's Equivalency column (OR-1027 — rounding to an integer
 *  made 1.5 read as "2" and the state total drift off the schedule
 *  total). */
function formatExposure(value: number): string {
    if (!Number.isFinite(value) || value === 0) return "";
    return value.toFixed(1);
}

export function useScheduleAggregates(submissionId: string | undefined) {
    const { state } = useFacilities(submissionId);
    const facilities = useMemo(
        () => (state.status === "ready" ? state.data : EMPTY_FACILITIES),
        [state],
    );
    const calcs = useFacilitiesCalc(facilities);

    const { setValue, getValues } = useFormContext<ScheduleFormData>();

    // Stable ref so the effect always sees the latest form helpers
    // without re-firing on identity changes.
    const formRef = useRef({ setValue, getValues });
    useEffect(() => {
        formRef.current = { setValue, getValues };
    }, [setValue, getValues]);

    useEffect(() => {
        // Top-level totals
        let totalEq = 0;
        let totalUnadj = 0;
        let totalSirAdj = 0;
        // State buckets
        const byState = new Map<string, { equivalency: number; unadjPremium: number }>();

        facilities.forEach((f, idx) => {
            const calc = calcs[idx];
            if (!calc) return;
            // Archived facilities are excluded from everything pricing-related
            // (OR-1431), so they contribute to neither the rate cards nor the
            // per-state exposure. Skipped here rather than by filtering the
            // input array, which would break the `calcs[idx]` alignment.
            if (f.isExcluded) return;
            totalEq += calc.equivalency;
            totalUnadj += calc.unadjPremium ?? 0;
            totalSirAdj += calc.sirAdjPremium ?? 0;

            const state = (f.state ?? "").trim();
            if (!state) return;
            const bucket = byState.get(state) ?? { equivalency: 0, unadjPremium: 0 };
            bucket.equivalency += calc.equivalency;
            bucket.unadjPremium += calc.unadjPremium ?? 0;
            byState.set(state, bucket);
        });

        const rateUnadjusted = totalEq > 0 ? totalUnadj / totalEq : 0;
        const rateAdjustedOccupancy = totalEq > 0 ? totalSirAdj / totalEq : 0;

        const distribution: StateDistributionRow[] = Array.from(byState.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([state, bucket]) => ({
                id: `state-${state}`,
                state,
                exposure: formatExposure(bucket.equivalency),
                rates:
                    bucket.equivalency > 0
                        ? formatRate(bucket.unadjPremium / bucket.equivalency)
                        : "",
            }));

        const sv = formRef.current.setValue;
        sv("rateUnadjusted", formatRate(rateUnadjusted), { shouldDirty: false });
        sv("rateAdjustedOccupancy", formatRate(rateAdjustedOccupancy), { shouldDirty: false });
        sv("stateDistribution", distribution, { shouldDirty: false });
    }, [facilities, calcs]);
}
